import { useState, useCallback } from 'react'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import {
  getSlotMachineStatus,
  performSpin,
  getSlotMachineHistory,
} from '../api'
import type { SlotMachineStats, SpinResult } from '../types'

export function useSlotMachineStatus() {
  return useQuery({
    queryKey: ['slot-machine-status'],
    queryFn: () => getSlotMachineStatus().then((res) => res.data),
    refetchOnWindowFocus: false,
  })
}

export function useSlotMachineHistory(page = 1, pageSize = 10) {
  return useQuery({
    queryKey: ['slot-machine-history', page, pageSize],
    queryFn: () => getSlotMachineHistory(page, pageSize).then((res) => res.data),
    refetchOnWindowFocus: false,
  })
}

export function useSlotMachineSpin() {
  const queryClient = useQueryClient()
  const { t } = useTranslation()

  return useMutation({
    mutationFn: () => performSpin(),
    onSuccess: (data) => {
      if (!data.success) {
        toast.error(data.message || t('Spin failed'))
        return
      }
      queryClient.invalidateQueries({ queryKey: ['slot-machine-status'] })
      queryClient.invalidateQueries({ queryKey: ['slot-machine-history'] })
    },
    onError: () => {
      toast.error(t('Spin failed, please try again'))
    },
  })
}

export interface SpinAnimationState {
  spinning: boolean
  currentSymbols: string[][]
  finalSymbols: string[] | null
  result: SpinResult | null
}

export function useSpinAnimation() {
  const [state, setState] = useState<SpinAnimationState>({
    spinning: false,
    currentSymbols: [['gpt', 'gpt', 'gpt'], ['claude', 'claude', 'claude'], ['gemini', 'gemini', 'gemini']],
    finalSymbols: null,
    result: null,
  })

  const startSpin = useCallback((result: SpinResult) => {
    const symbols = ['gpt', 'claude', 'gemini', 'deepseek', 'qwen', 'spark', 'glm', 'yi', 'doubao', 'bomb']

    const getRandomSymbol = () => symbols[Math.floor(Math.random() * symbols.length)]

    const initialColumns = Array.from({ length: 3 }, () =>
      Array.from({ length: 3 }, () => getRandomSymbol())
    )

    setState({
      spinning: true,
      currentSymbols: initialColumns,
      finalSymbols: result.symbols,
      result,
    })
  }, [])

  const updateSpinningFrame = useCallback((columns: string[][]) => {
    setState((prev) => ({
      ...prev,
      currentSymbols: columns,
    }))
  }, [])

  const stopSpin = useCallback(() => {
    setState((prev) => ({
      ...prev,
      spinning: false,
    }))
  }, [])

  return { state, startSpin, updateSpinningFrame, stopSpin }
}
